import { zodResolver } from "@hookform/resolvers/zod";
import { router, usePage } from "@inertiajs/react";
import { useMediaQuery, useSessionStorage } from "@uidotdev/usehooks";
import { format, startOfDay } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { orderMitraReason } from "@/components/admin-portal/appointment/mitra-assignment-reason";
import {
	DEFAULT_VALUES_THERAPIST,
	owesMitraReason,
} from "@/lib/appointments/form";
import {
	type AppointmentRescheduleSchema,
	buildPayload,
	defineFormDefaultValues,
	RESCHEDULE_APPOINTMENT_FORM_SCHEMA,
} from "@/lib/appointments/form-reschedule";
import { IS_DEKSTOP_MEDIA_QUERY } from "@/lib/constants";
import { SESSION_ISOLINE_KEY, SESSION_MARKERS_KEY } from "@/lib/here-maps";
import i18n from "@/lib/i18n";
import { goBackHandler, populateQueryParams } from "@/lib/utils";
import type { AppointmentRescheduleGlobalPageProps } from "@/pages/AdminPortal/Appointment/Reschedule";
import { SESSION_STORAGE_FORM_SELECTIONS_KEY } from "./use-appointment-form";
import {
	type TherapistOption,
	usePreferredTherapistGender,
	useTherapistAvailability,
} from "./use-appointment-utils";

export const useRescheduleForm = () => {
	const { url: pageURL, props: globalProps } =
		usePage<AppointmentRescheduleGlobalPageProps>();

	// * define the state management for form
	const [isLoading, setIsLoading] = useState(false);
	const formDefaultvalues = useMemo(
		() =>
			defineFormDefaultValues(
				globalProps.appointment,
				globalProps.mitraReason?.reasons[String(globalProps.appointment.id)],
			),
		[globalProps.appointment, globalProps.mitraReason],
	);
	const form = useForm<AppointmentRescheduleSchema>({
		resolver: zodResolver(RESCHEDULE_APPOINTMENT_FORM_SCHEMA),
		defaultValues: { ...formDefaultvalues },
		mode: "onChange",
	});
	// `disabled={isLoading}` engages only after a re-render, so a second click in
	// the same tick still submits and writes a second history row. Same guard as
	// the booking form in New.tsx.
	const isSubmittingRef = useRef(false);
	const onSubmit = useCallback(
		(values: AppointmentRescheduleSchema) => {
			if (isSubmittingRef.current) return; // a submit is already in flight
			if (
				globalProps.mitraReason &&
				!orderMitraReason(globalProps.mitraReason) &&
				!values.mitraAssignmentReason &&
				owesMitraReason(globalProps.appointment.therapist, values.therapist)
			) {
				form.setError("mitraAssignmentReason", {
					type: "custom",
					message: i18n.t(
						"appt_schedule.fields.mitra_assignment_reason.errors.required",
						{ ns: "appointments-form" },
					),
				});
				return;
			}

			console.group();
			console.log("Starting process to reschedule the appointment...");

			const baseURL =
				globalProps.adminPortal.router.adminPortal.appointment.index;
			const id = globalProps.appointment.id;
			// Appointments tied to an order are managed by the external API, so
			// route them through the external reschedule endpoint (mirrors the
			// cancel/cancel-external pattern). Order-less appointments fall back
			// to the internal reschedule logic.
			const hasOrder = !!globalProps.appointment.order?.id;
			const submitURL = hasOrder
				? `${baseURL}/${id}/reschedule-external`
				: `${baseURL}/${id}/reschedule`;
			const submitConfig = {
				preserveScroll: true,
				preserveState: true,
				replace: false,
				only: ["adminPortal", "flash", "errors", "appointment"],
				onStart: () => setIsLoading(true),
				onFinish: () => {
					isSubmittingRef.current = false;
					setTimeout(() => setIsLoading(false), 250);
				},
			} satisfies Parameters<typeof router.put>["2"];
			const payload = buildPayload(values);

			isSubmittingRef.current = true;
			setIsLoading(true); // disable the button now, not only on onStart
			router.put(submitURL, { appointment: payload }, submitConfig);

			console.log("Appointment successfully rescheduled...");
			console.groupEnd();
		},
		[globalProps, form.setError],
	);

	// * side-effect for server validation
	useEffect(() => {
		if (!globalProps?.errors) return;

		for (const [key, value] of Object.entries(globalProps?.errors)) {
			form.setError(key === "therapistId" ? "therapist.name" : (key as any), {
				type: "custom",
				message: (value as string[]).join(", "),
			});
		}
	}, [globalProps.errors, form.setError]);

	/**
	 * * watching the changed of the route
	 * * then show the dialog for the confirmation to user if user want to navigate away from the page
	 * * if navigated then we remove the stored form data in session storage
	 *
	 * ? isNavigateConfirm is required to prevent the confirmation modal from appearing twice.
	 */
	const [isNavigateConfirm, setIsNavigateConfirm] = useState(false);
	const removeStorage = useCallback(() => {
		window.sessionStorage.removeItem(SESSION_ISOLINE_KEY);
		window.sessionStorage.removeItem(SESSION_MARKERS_KEY);
	}, []);
	useEffect(() => {
		// Add a listener for route changes
		return router.on("before", (event) => {
			if (isNavigateConfirm) return;

			const url = event.detail.visit.url;
			const path = url.pathname;
			const { queryParams } = populateQueryParams(url.href);
			const isRescheduled = queryParams?.rescheduled;

			// Determine if the path is the root, a parent root, or the current root
			const isRoot = path === "/";
			const isParentRoot =
				globalProps.adminPortal.router.adminPortal.appointment.index.includes(
					path,
				);
			const isCurrentRoot = pageURL.includes(path);
			const isReschedulePath = path.includes("reschedule");

			// If the path is not root, or parent root, or current root, or is the reschedule path (reschedule path mean to POST to save the appointment) do nothing
			if (
				!isRoot &&
				(!isParentRoot || isRescheduled) &&
				(isCurrentRoot || isReschedulePath)
			)
				return;

			// Log the URL being visited
			console.log(`Starting a visit to ${url}`);

			// Confirm navigation away from the current page
			if (confirm("Are you sure you want to navigate away?")) {
				// Confirm navigation away from the current page
				setIsNavigateConfirm(true);
				// Remove the appointment form data from session storage
				removeStorage();
			} else {
				event?.preventDefault();
			}
		});
	}, [
		globalProps.adminPortal.router.adminPortal.appointment.index,
		pageURL,
		isNavigateConfirm,
		removeStorage,
	]);
	/**
	 * * watching the success of route navigation
	 * * then we delete the form data stored in the session storage
	 * * this side effect is useful because after we book an appointment then the form will be calculated to redirect the user
	 * * also useful if the user clicks the redirect button now
	 */
	useEffect(() => {
		return router.on("navigate", (event) => {
			// Determine if the path is the current root (user on reload)
			const isCurrentRoot = event.detail.page.url.includes("reschedule_page");
			const isReschedulePath = event.detail.page.url.includes("reschedule");

			// If the path is current root (user on reload), or is the book path (book path mean to POST to save the appointment) do nothing
			if (isCurrentRoot || isReschedulePath) return;

			// Remove the appointment form data from session storage
			removeStorage();

			console.log(`Navigated to ${event.detail.page.url}`);
		});
	}, [removeStorage]);

	return { form, isLoading, onSubmit };
};

export const useFormActionButtons = () => {
	const isDekstop = useMediaQuery(IS_DEKSTOP_MEDIA_QUERY);
	const onBackRoute = useCallback(() => {
		goBackHandler();
	}, []);

	return { isDekstop, onBackRoute };
};

export const useRescheduleFields = () => {
	const { props: globalProps, url: pageURL } =
		usePage<AppointmentRescheduleGlobalPageProps>();
	const form = useFormContext<AppointmentRescheduleSchema>();
	const appointment = useMemo(
		() => globalProps.appointment,
		[globalProps.appointment],
	);
	// Continuity anchor for the calendar picker = the series lead (visit 1)
	// therapist, resolved server-side. Shaped to the SchedulePicker prop
	// ({ id: string; name; employmentType }); null → no anchor (show-all).
	const continuityTherapist = useMemo(() => {
		const ct = globalProps.optionsData?.continuityTherapist;
		return ct
			? {
					id: String(ct.id),
					name: ct.name,
					employmentType: ct.employmentType ?? undefined,
				}
			: null;
	}, [globalProps.optionsData?.continuityTherapist]);
	const serviceId = useMemo(
		() => appointment.service?.id || "",
		[appointment.service?.id],
	);
	const patientDetails = useMemo(() => {
		const patient = appointment?.patient;
		const visitAddress = appointment?.visitAddress;

		return {
			fullName: patient?.name || "",
			address: visitAddress?.addressLine || "",
			latitude: visitAddress?.latitude || 0,
			longitude: visitAddress?.longitude || 0,
			locationId: visitAddress?.locationId || "",
		};
	}, [appointment?.patient, appointment?.visitAddress]);
	const watchPreferredTherapistGenderValue = useWatch({
		control: form.control,
		name: "preferredTherapistGender",
	});
	const watchAppointmentDateTimeValue = useWatch({
		control: form.control,
		name: "appointmentDateTime",
	});
	const watchAllOfDayValue = useWatch({
		control: form.control,
		name: "formOptions.findTherapistsAllOfDay",
	});
	const [isLoading, setIsLoading] = useState({ therapists: false });
	const errorsServerValidation = useMemo(
		() => (globalProps?.errors?.fullMessages as unknown as string[]) || null,
		[globalProps?.errors?.fullMessages],
	);

	// * for therapist availability and isoline maps
	const [formSelections, setFormSelections] = useSessionStorage<{
		therapist: null | TherapistOption;
	}>(SESSION_STORAGE_FORM_SELECTIONS_KEY, {
		therapist: null,
	});

	// Track if this is the initial load to prevent re-initialization after user reset
	const isInitialLoad = useRef(true);

	// Initialize formSelections.therapist from appointment.therapist when form loads
	// This ensures the default therapist is pre-selected but can be reset
	useEffect(() => {
		// Only initialize on initial load and if appointment has therapist
		if (isInitialLoad.current && appointment?.therapist) {
			setFormSelections({
				therapist: {
					id: appointment.therapist.id,
					name: appointment.therapist.name,
					registrationNumber: appointment.therapist.registrationNumber || "",
					employmentStatus: appointment.therapist.employmentStatus || "ACTIVE",
					// Include other relevant therapist properties if needed
					...(appointment.therapist.gender && {
						gender: appointment.therapist.gender,
					}),
					...(appointment.therapist.employmentType && {
						employmentType: appointment.therapist.employmentType,
					}),
				},
			});
			isInitialLoad.current = false;
		}
	}, [appointment?.therapist, setFormSelections]);
	const coordinate = useMemo(
		() => [patientDetails.latitude, patientDetails.longitude],
		[patientDetails.latitude, patientDetails.longitude],
	);
	const mapAddress = useMemo(() => {
		const location = appointment?.location;
		const visitAddress = appointment?.visitAddress;

		return {
			country: location?.country || "",
			state: location?.state || "",
			city: location?.city || "",
			postalCode: visitAddress?.postalCode || "",
			address: visitAddress?.addressLine || "",
		};
	}, [appointment?.visitAddress, appointment?.location]);
	const onChangeTherapistLoading = useCallback((value: boolean) => {
		setIsLoading((prev) => ({ ...prev, therapists: value }));
	}, []);
	const onResetTherapistFormValue = useCallback(() => {
		// Use setValue instead of resetField for more reliable clearing
		form.setValue("therapist", DEFAULT_VALUES_THERAPIST);
	}, [form.setValue]);
	const onSelectTherapist = useCallback(
		(value: Partial<{ id: string | number; name: string }> | null) => {
			form.setValue("therapist", value, {
				shouldValidate: true,
			});
		},
		[form.setValue],
	);
	const {
		onResetIsoline,
		onResetTherapistOptions,
		...therapistAndIsolineValues
	} = useTherapistAvailability({
		serviceIdValue: serviceId,
		appointmentDateTImeValue: watchAppointmentDateTimeValue,
		preferredTherapistGenderValue: watchPreferredTherapistGenderValue,
		isAllOfDayValue: !!watchAllOfDayValue,
		patientValues: {
			fullName: patientDetails.fullName,
			address: patientDetails.address,
			latitude: patientDetails.latitude,
			longitude: patientDetails.longitude,
			locationId: patientDetails.locationId,
		},
		fetchURL: pageURL,
		formType: "reschedule",
		onChangeTherapistLoading,
		onResetTherapistFormValue,
	});
	// Function to reset therapist to appointment default
	const resetTherapistToDefault = useCallback(() => {
		if (appointment?.therapist) {
			setFormSelections({
				therapist: {
					id: appointment.therapist.id,
					name: appointment.therapist.name,
					registrationNumber: appointment.therapist.registrationNumber || "",
					employmentStatus: appointment.therapist.employmentStatus || "ACTIVE",
					...(appointment.therapist.gender && {
						gender: appointment.therapist.gender,
					}),
					...(appointment.therapist.employmentType && {
						employmentType: appointment.therapist.employmentType,
					}),
				},
			});
			// Also reset form value to match
			onSelectTherapist({
				id: appointment.therapist.id,
				name: appointment.therapist.name,
			});
		}
	}, [appointment?.therapist, setFormSelections, onSelectTherapist]);

	// reset the therapist availability field value, options values from server fetch data and isoline map
	const onResetAllTherapistState = useCallback(() => {
		onResetTherapistFormValue();
		onResetTherapistOptions();
		onResetIsoline();
		// Clear formSelections completely
		setFormSelections({ therapist: null });
	}, [
		onResetTherapistFormValue,
		onResetTherapistOptions,
		onResetIsoline,
		setFormSelections,
	]);
	const therapistAvailabilityHooks = {
		...therapistAndIsolineValues,
		coordinate,
		mapAddress,
		onSelectTherapist,
		onResetAllTherapistState,
		formSelections,
		setFormSelections,
	};

	// * for preferred therapist gender field
	const prefGenderHooks = usePreferredTherapistGender({
		sourceOptions: globalProps.optionsData?.preferredTherapistGender,
	});

	// * for appointment date time min max values and disabled visits
	const apptDateTime = useMemo(() => {
		const {
			min: minIso,
			max: maxIso,
			message = null,
			disabledVisits = [],
		} = globalProps.optionsData?.apptDateTime ?? {};

		return {
			min: minIso ? new Date(minIso) : null,
			max: maxIso ? new Date(maxIso) : null,
			message,
			disabledVisits,
		};
	}, [globalProps.optionsData?.apptDateTime]);
	const selectedTimeSlot = useMemo(() => {
		if (!watchAppointmentDateTimeValue) return null;
		return format(watchAppointmentDateTimeValue, "HH:mm");
	}, [watchAppointmentDateTimeValue]);
	const onSelectAllOfDay = useCallback(
		(value: boolean) => {
			const selectedDate = form.getValues("appointmentDateTime");
			form.setValue("formOptions.findTherapistsAllOfDay", value);
			if (selectedDate) {
				form.setValue("appointmentDateTime", startOfDay(selectedDate), {
					shouldValidate: false,
				});
			}
		},
		[form.setValue, form.getValues],
	);
	const onSelectTimeSlot = useCallback(
		(value: string) => {
			const date = form.getValues("appointmentDateTime");
			if (date) {
				const [hours, minutes] = value.split(":");
				const newDate = new Date(date);
				newDate.setHours(Number.parseInt(hours), Number.parseInt(minutes));
				form.setValue("appointmentDateTime", newDate);
			}
		},
		[form.getValues, form.setValue],
	);
	const apptHooksValue = {
		apptDateTime,
		watchAppointmentDateTimeValue,
		watchAllOfDayValue,
		selectedTimeSlot,
		onSelectAllOfDay,
		onSelectTimeSlot,
	};

	return {
		...prefGenderHooks,
		...therapistAvailabilityHooks,
		...apptHooksValue,
		form,
		isLoading,
		appointment,
		continuityTherapist,
		errorsServerValidation,
		resetTherapistToDefault,
		// Exposed for the calendar SchedulePicker, which needs the visit's
		// location, service and coordinates to query therapist availability,
		// plus the gender filter it forwards to /available.
		serviceId,
		patientDetails,
		watchPreferredTherapistGenderValue,
	};
};
